import React, { useCallback, useState, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Body2, Button, Dialog, Header3, Label, SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";
import SelectDateUserDaysOff from "~/components/SelectDate/SelectDateUserDaysOff.jsx";
import { addAvailabilityRecord } from "~/store/actions/actions.availabilityRecords.jsx";
import { getAllAvailabilityTypesAsArray } from "~/store/selectors/selector.availabilityTypes.jsx";
import { makeIsLoggedUserOwnerSelector, getNumberOfOwners } from "~/store/selectors/selector.users.jsx";
import useAcSelectDate from "../Hooks/useAcSelectDate";
import TextareaProgress from "../Inputs/TextareaProgress";
import SelectContainer from "../SelectContainer/SelectContainer";
import { showErrorToast, showSuccessToast } from "../Toast";

const UserAvailabilityRecordModalContentContainer = ({
  forOwners,
  onClose,
  userName,
  userId,
  userIsLoggedUser,
  open,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dates, formatDates, handleDateChange, today } = useAcSelectDate();

  const [availablilityTypeId, setAvailabilityTypeId] = useState(0);
  const [message, setMessage] = useState("");

  const getIsLoggedUserOwner = useMemo(makeIsLoggedUserOwnerSelector, []);
  const isLoggedUserOwner = useSelector(getIsLoggedUserOwner);
  const availabilityTypes = useSelector(getAllAvailabilityTypesAsArray);
  const numberOfOwners = useSelector(getNumberOfOwners);

  const hideMessageBox = userIsLoggedUser && isLoggedUserOwner && numberOfOwners === 1;

  const defaultDates = useMemo(() => {
    return {
      from: today.unix(),
      to: today.unix(),
    };
  }, [today]);

  const handleAvailabilityTypeChange = useCallback((id) => {
    setAvailabilityTypeId(id);
  }, []);

  const handleMessageChange = useCallback((e) => {
    setMessage(e.target.value);
  }, []);

  const handleResetForm = useCallback(() => {
    handleDateChange(defaultDates);
    setAvailabilityTypeId(0);
    setMessage("");
  }, [defaultDates, handleDateChange]);

  const handleClose = useCallback(() => {
    handleResetForm();
    return typeof onClose === "function" && onClose();
  }, [onClose, handleResetForm]);

  const handleSubmit = useCallback(() => {
    if (!availablilityTypeId) {
      showErrorToast(t("Availability type is missing"));
    } else {
      dispatch(
        addAvailabilityRecord(userId, {
          availability_type_id: availablilityTypeId,
          start_date: formatDates.from,
          end_date: formatDates.to,
          message,
        })
      ).then((res) => {
        if (!res.error) {
          showSuccessToast(t("Record added"));
          return handleClose();
        }
      });
    }
  }, [availablilityTypeId, dispatch, formatDates.from, formatDates.to, handleClose, message, t, userId]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <Dialog.Title disableDefaultHeading>
        <Header3>{t("New Availability Record")}</Header3>
        <Body2>{userName}</Body2>
      </Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        <div className="tw-mb-6">
          <Label
            className="tw-block tw-mb-1"
            size="small"
          >
            {t("Date")}
          </Label>
          <SelectDateUserDaysOff
            trigger={(text) => <SelectTrigger className="tw-w-full tw-max-w-[300px]">{text}</SelectTrigger>}
            userId={userId}
            selectedDays={dates}
            defaultLabelText={`${t("Add")}...`}
            onSave={handleDateChange}
            type="selectable_global_day_off"
            weekendIsSelectable
            defaultTimezoneAware
            required
          />
        </div>
        <div className="tw-mb-6">
          <Label
            className="tw-block tw-mb-1"
            size="small"
          >
            {t("Type")}
          </Label>
          <SelectContainer
            className="tw-w-full tw-max-w-[300px]"
            options={availabilityTypes}
            selected={availablilityTypeId}
            onChange={handleAvailabilityTypeChange}
            placeholder={t("Select")}
            noResultText={t("No result")}
            disableSearch
            disabledInternalSort
          />
        </div>
        {!hideMessageBox && (
          <div>
            <Label
              htmlFor="availability-message-for"
              className="tw-block tw-mb-1"
              size="small"
            >
              {forOwners ? t("Message for Owners") : <Trans>Message for {{ userName }}</Trans>}
            </Label>
            <TextareaProgress
              id="availability-message-for"
              className="tw-w-full"
              textareaClassName="tw-w-full"
              value={message}
              onChange={handleMessageChange}
              maxNumChars={191}
              maxReachedClass="circle-progress--error"
              warningClass="circle-progress--warning"
            />
          </div>
        )}
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          type="submit"
          variant="primary"
          className="tw-mr-4"
          onClick={handleSubmit}
        >
          {t("Send")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
        >
          {t("Cancel")}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

UserAvailabilityRecordModalContentContainer.propTypes = {
  userName: PropTypes.string.isRequired,
  forOwners: PropTypes.bool,
  onClose: PropTypes.func,
  hideMessageBox: PropTypes.bool,
  userId: PropTypes.number,
  userIsLoggedUser: PropTypes.bool,
  open: PropTypes.bool,
};

UserAvailabilityRecordModalContentContainer.defaultProps = {
  forOwners: false,
  userIsLoggedUser: false,
  open: false,
};

export default UserAvailabilityRecordModalContentContainer;
